// export-html.ts — the standalone-HTML rendering layer for note export, extracted
// verbatim from notes/export-import.ts (Modernization Tier 7 monolith split). Turns a
// note's markdown into a self-contained HTML document (export theme CSS + KaTeX, open-
// equation pre-eval, table-column materialization, the headless render-core passes,
// attachment embedding, single-note / notebook templates). Calls no export/backup/import
// operation; export-import.ts imports the entry points back (one-way, no cycle).

import {
  buildMathVariableMap,
  buildMermaidVarMap,
  evaluateInlineWithColumns,
  extractAllMathExpressions,
  formatMathResult,
  isEvaluableMath,
} from '../editor/math-eval.js';
import { setupCollapsibleHeadings } from '../editor/render-core/collapsible.js';
import { installMarkedConfig } from '../editor/render-core/marked-config.js';
// Headless render passes — imported from render-core directly (not the
// interactive markdown-renderer.js orchestrator) so export/import/backup never
// drag in the on-screen preview surface, graph view, or theme engine.
import { ensureKatexLoaded, renderMathIn } from '../editor/render-core/math.js';
import { renderMermaidDiagrams } from '../editor/render-core/mermaid.js';
import { styleTaskListItems } from '../editor/render-core/post-process.js';
import { preprocessMarkdown } from '../editor/render-core/preprocess.js';
import { buildColumnEnv } from '../editor/table-column-env.js';
import { alignTableColumns } from '../editor/table-features.js';
import { TableSource } from '../editor/table-source.js';
// The pure inner-strip lives in the math-expr leaf (imported directly — it's a
// zero-app-import leaf, no editor chain); `stripBakedOpenEquations` below walks
// the note with it to invert the export bake for the import path.
import { stripBakedInner } from '../utils/math-expr.js';
import { escapeHtml, safeRenderMarkdown } from '../utils/sanitize.js';
import { mimeForExtension } from './attachment-helpers.js';

// BinaryUtil + DateUtil were previously sourced via window.* for legacy
// call sites. No references remain in this module; named exports from
// utils/binary.js + utils/dates.js are imported directly at call sites.

// Escape user-supplied strings before inserting into HTML markup.  Note names
// can land in either text or attribute contexts here, so we always escape the
// double-quote too.
/**
 * @param {any} str
 * @returns {string} attribute-safe escaped HTML.
 */
function _escAttr(str: any) {
  return escapeHtml(str, { forAttr: true });
}

// Derive a note name from the first-line # header, falling back to the
// file's base name when no header is present.
/**
 * @param {string} fileBaseName
 * @param {string} content
 * @returns {string} the note name from the first-line header, or the base name.
 */
export function _noteNameFromContent(fileBaseName: any, content: any) {
  const firstLine = content.split('\n')[0].trim();
  if (firstLine.startsWith('#')) {
    const title = firstLine
      .replace(/^#+\s*/, '')
      .replace(/\s*>\s*$/, '')
      .trim();
    if (title) return title;
  }
  return fileBaseName;
}

// Return the KaTeX stylesheet block for the exported HTML. Math has
// already been rendered to inline HTML inside the container before this
// runs, so the export just needs KaTeX's stylesheet to lay out the
// rendered DOM. When the in-document KaTeX stylesheet isn't available
// (e.g. the user is exporting a note whose preview never loaded math),
// fall back to a CDN <link>; the export is otherwise unstyled.
/**
 * @param {any} katexCSS
 * @returns {string} the KaTeX stylesheet block for the export.
 */
function _mathStyleSection(katexCSS: any) {
  return katexCSS
    ? `<style>${katexCSS}</style>`
    : `<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.17.0/dist/katex.min.css">`;
}

// ── Export rendering helpers ───────────────────────────────────────────────

// Reads current CSS custom properties and returns a :root block string so
// exported HTML matches whatever theme the user has active.
/** @returns {string} a :root CSS block matching the active theme. */
export function getExportThemeCSSRoot() {
  const s = getComputedStyle(document.documentElement);
  /** @param {string} p @param {string} fb @returns {string} */
  const v = (p: any, fb: any) => (s.getPropertyValue(p) || '').trim() || fb;
  return [
    `--bg: ${v('--bg', '#1e1e1e')}`,
    `--text: ${v('--text', '#e8dcf4')}`,
    `--accent: ${v('--accent', '#a272b0')}`,
    `--surface: ${v('--surface', '#2e2e2e')}`,
    `--border: ${v('--border', '#444')}`,
    `--h1: ${v('--h1', '#c89fdf')}`,
    `--h2: ${v('--h2', '#b98fd0')}`,
    `--h3: ${v('--h3', '#a47fc0')}`,
    `--h4: ${v('--h4', '#9370b0')}`,
    `--h5: ${v('--h5', '#8060a0')}`,
    `--h6: ${v('--h6', '#6d5090')}`,
    `--link: ${v('--link', '#9cdcfe')}`,
    `--bold: ${v('--bold', '#f0e6ff')}`,
    `--italic: ${v('--italic', '#c8a0e0')}`,
    `--strike: ${v('--strike', '#7a6a8a')}`,
    `--code: ${v('--code', '#9ec7b5')}`,
    `--code-bg: ${v('--code-bg', '#2a2a3a')}`,
    `--code-block-bg: ${v('--code-block-bg', '#252535')}`,
    `--code-block-border: ${v('--code-block-border', '#3a3a5a')}`,
    `--mark-bg: ${v('--mark-bg', '#3a1060')}`,
    `--mark-color: ${v('--mark-color', '#c89fdf')}`,
    `--blockquote-border: ${v('--blockquote-border', '#a272b0')}`,
    `--blockquote-bg: ${v('--blockquote-bg', '#2a2040')}`,
    `--blockquote-text: ${v('--blockquote-text', '#b8a8cc')}`,
    `--table-header-bg: ${v('--table-header-bg', '#2a2040')}`,
    `--table-alt-row: ${v('--table-alt-row', '#242030')}`,
    `--hr: ${v('--hr', '#6b4e7a')}`,
    `--footnote: ${v('--footnote', '#a272b0')}`,
    `--footnote-back: ${v('--footnote-back', '#6b4e7a')}`,
  ].join('; ');
}

// Locate every markdown table block in `lines`: a `|…` header row immediately
// followed by a `|---|` separator, then its consecutive `|…` body rows. Returns
// inclusive `{ start, end }` line-index ranges. Shared by the column-env builder
// and the computed-column bake so both surfaces scan tables identically.
function findTableBlocks(lines: string[]): { start: number; end: number }[] {
  const blocks: { start: number; end: number }[] = [];
  for (let i = 0; i < lines.length; i++) {
    if (
      TableSource.isTableRowLine(lines[i]) &&
      i + 1 < lines.length &&
      TableSource.isTableSeparatorLine(lines[i + 1])
    ) {
      let j = i + 2;
      while (j < lines.length && TableSource.isTableRowLine(lines[j])) j++;
      blocks.push({ start: i, end: j - 1 });
      i = j - 1;
    }
  }
  return blocks;
}

// Build the HEADLESS note-wide column env from a markdown string: the
// `{ name: number | number[] }` namespace of scalars + literal data columns +
// resolved formula columns (the resolveColumns/_computeTableMathEnv model the
// live editor's `tableMathEnvField` produces). This is the export/backup
// equivalent of the editor's StateField — the export path has NO editor state,
// so it MUST rebuild the env here (don't try to "unify" it with the editor's
// cached env; see the B5-Layer-1 note in markdown-renderer/math-eval). Shared by
// `preEvaluateOpenEquations` (column-aware inline math) and
// `materializeTableColumns` (computed-column bake-in) so both surfaces resolve
// columns identically. Returns `{ env, values }` (resolveColumns output) or an
// empty env when there are no tables. Pure string → object; no DOM, no editor.
/**
 * @param {any} markdown
 * @returns {any} resolveColumns output { env, values }, or an empty env.
 */
function _buildNoteColumnEnv(markdown: any) {
  const empty = { env: {}, values: {} };
  if (typeof markdown !== 'string' || markdown.indexOf('|') === -1) return empty;
  if (!TableSource || typeof TableSource.parseTableSource !== 'function') return empty;
  const { parseTableSource } = TableSource;

  const lines = markdown.split('\n');
  const blocks = findTableBlocks(lines);
  if (blocks.length === 0) return empty;

  // Discovery (markdown line-scan) stays here; the column-env reduction — claim
  // names, build number[] columns, extract formula headers, shadow scalars,
  // resolve — is the shared buildColumnEnv (deduped with the editor's
  // table-math-env field). Export discards the warnings (no widget to render
  // them into); the column-wins-over-scalar shadow + NaN-cell mapping it
  // enforces are the cross-surface invariants the bake step relies on.
  const tables = [];
  for (const b of blocks) {
    const parsed = parseTableSource(lines.slice(b.start, b.end + 1).join('\n'));
    if (parsed) tables.push(parsed);
  }
  const scalars = buildMathVariableMap(extractAllMathExpressions(markdown));
  const { env, values } = buildColumnEnv({ tables, scalars });
  return { env, values };
}

// Walk every `$…$` / `$$…$$` math span in `markdown` and rewrite each span's
// INNER via `transformInner(inner, evalEnv, expr)` (return `inner` unchanged to
// skip). Spans are visited highest-index-first so earlier offsets stay valid
// across the in-place splices — the descending-position inner-slice shared by
// the export bake (preEvaluateOpenEquations) and the import strip
// (stripBakedOpenEquations). Pure string → string; no DOM, no editor.
function walkMathSpanInners(
  markdown: string,
  evalEnv: any,
  transformInner: (inner: string, evalEnv: any, expr: any) => string
): string {
  if (typeof extractAllMathExpressions !== 'function') return markdown;
  const exprs = extractAllMathExpressions(markdown);
  if (exprs.length === 0) return markdown;
  // Process highest index first so earlier positions stay valid.
  const sorted = exprs.slice().sort((a, b) => b.index - a.index);
  let result = markdown;
  for (const expr of sorted) {
    if (expr.type === 'block') {
      const innerStart = expr.index + 2;
      const innerEnd = result.indexOf('$$', innerStart);
      if (innerEnd === -1) continue;
      const inner = result.slice(innerStart, innerEnd);
      const newInner = transformInner(inner, evalEnv, expr);
      if (newInner === inner) continue;
      result = result.slice(0, innerStart) + newInner + result.slice(innerEnd);
    } else {
      let j = expr.index + 1;
      while (j < result.length) {
        if (result[j] === '\\') {
          j += 2;
          continue;
        }
        if (result[j] === '$') break;
        j++;
      }
      if (j >= result.length) continue;
      const inner = result.slice(expr.index + 1, j);
      const newInner = transformInner(inner, evalEnv, expr);
      if (newInner === inner) continue;
      result = result.slice(0, expr.index + 1) + newInner + result.slice(j);
    }
  }
  return result;
}

// Build the eval namespace shared by the bake + strip walks: scalars from the
// inline defs (the cheap `isEvaluableMath` filter arg) under the note-wide
// column env (which already shadows collisions). The column env is the
// authority; the scalar varMap fills entries it doesn't define.
function _buildEvalEnv(markdown: string, prebuilt?: any): any {
  const varMap = buildMathVariableMap(extractAllMathExpressions(markdown));
  const { env } = prebuilt || _buildNoteColumnEnv(markdown);
  return Object.assign({}, varMap, env);
}

// Pre-fills results of open (trailing "=") equations into the markdown so
// the exported HTML shows solved values without needing math-eval.js.
// Column-aware: routes through THE shared inline-with-columns evaluator
// (evaluateInlineWithColumns) against the note-wide column env, so an inline
// prose formula reading a named table column (`$total = sum(Price)$`) bakes the
// IDENTICAL scalar the editor click + preview produce. A whole-VECTOR result
// (`$x = Price$`) is left unresolved (no bake) — inline can't host a column.
/**
 * @param {any} markdown
 * @param {any} [prebuilt] - a pre-built column env to reuse.
 * @returns {any} the markdown with open inline equations baked.
 */
export function preEvaluateOpenEquations(markdown: any, prebuilt: any) {
  return walkMathSpanInners(markdown, _buildEvalEnv(markdown, prebuilt), (inner, evalEnv, expr) => {
    const tex = expr.tex.trim();
    // Open-equation gate (structural, with scalar varMap). The shared evaluator
    // then resolves against the column env: a scalar result bakes; a vector or
    // unresolved result is skipped (left as `?` for the reader to see).
    if (!isEvaluableMath(tex)) return inner;
    const result = evaluateInlineWithColumns(tex, evalEnv);
    if (!result.ok) return inner; // unresolved OR vector → don't bake
    return inner.replace(/=\s*$/, `= ${result.formatted}`);
  });
}

// Invert the export bake for the IMPORT path (#5): a baked `$expr = value$`
// becomes `$expr =$` again so a re-imported note computes LIVE. Mirrors
// `preEvaluateOpenEquations`'s walk (same env build + inner-slice logic), but
// applies the pure `stripBakedInner` to every span's inner — which itself gates:
// it strips ONLY a `lhs = value` whose recomputed `lhs` equals `value` (so it's
// provably our bake), and leaves authored assignments (`$x = 5$`), stale values,
// and already-open `$expr =$` untouched. Idempotent (stripping twice == once).
// Pure string → string; no DOM, no editor.
/**
 * @param {any} markdown
 * @returns {any} the markdown with baked open-equation answers stripped.
 */
export function stripBakedOpenEquations(markdown: any) {
  if (typeof markdown !== 'string') return markdown;
  // Same eval namespace the bake used: scalars from the inline defs + the column
  // env (which shadows collisions), so the recompute matches the baked value.
  return walkMathSpanInners(markdown, _buildEvalEnv(markdown), (inner, evalEnv) =>
    stripBakedInner(inner, evalEnv)
  );
}

// Bake computed-column values into a markdown string for export / backup.
// Finds every table, treats `$name = expr$` headers as computed columns and
// plain identifier headers as data columns (note-wide-unique names + inline
// scalars — the same model the live editor uses), resolves them, and fills the
// computed columns' body cells with the formatted values while KEEPING the
// formula header — so the exported markdown shows the numbers AND round-trips
// back into a live computed note. Pure string → string; no DOM, no editor.
// Single-build export pipeline: resolve the note-wide column env ONCE and
// thread it through both bake passes. The column values are identical before
// and after the inline pre-eval (that pass only rewrites open inline equations,
// never table headers or scalar defs), so reusing the env is safe and avoids
// building it twice per exported note (the backup loop runs this per note).
/**
 * @param {any} content
 * @returns {any} the markdown with inline + computed-column values baked.
 */
export function processNoteForExport(content: any) {
  const built = _buildNoteColumnEnv(content);
  return materializeTableColumns(preEvaluateOpenEquations(content, built), built);
}

/**
 * @param {any} markdown
 * @param {any} [prebuilt]
 * @returns {any} the markdown with computed-column body cells filled.
 */
export function materializeTableColumns(markdown: any, prebuilt: any) {
  if (typeof markdown !== 'string' || markdown.indexOf('|') === -1) return markdown;
  if (!TableSource || typeof TableSource.parseTableSource !== 'function') return markdown;
  const { parseTableSource, serializeTable, displayCell, storeCell, parseColumnFormula } =
    TableSource;

  const lines = markdown.split('\n');
  const blocks = findTableBlocks(lines);
  if (blocks.length === 0) return markdown;

  // Resolve the note-wide env (data columns + formula columns + inline scalars)
  // via the shared headless builder, so the computed-column bake-in and the
  // inline-math pre-eval (preEvaluateOpenEquations) agree on every value.
  const { values } = prebuilt || _buildNoteColumnEnv(markdown);
  const hasFormula = blocks.some((b) => {
    const parsed = parseTableSource(lines.slice(b.start, b.end + 1).join('\n'));
    return parsed?.header.some((h: any) => parseColumnFormula(displayCell(h)));
  });
  if (!hasFormula) return markdown; // no computed columns to bake

  // Fill computed columns; splice blocks back in reverse so earlier line
  // indices stay valid even if a serialize changes the line count.
  for (let bi = blocks.length - 1; bi >= 0; bi--) {
    const b = blocks[bi];
    const parsed = parseTableSource(lines.slice(b.start, b.end + 1).join('\n'));
    if (!parsed) continue;
    let touched = false;
    parsed.header.forEach((h: any, col: any) => {
      const f = parseColumnFormula(displayCell(h));
      if (!f) return;
      const vec = values[f.name];
      parsed.body.forEach((row: any, r: any) => {
        const v = Array.isArray(vec) ? vec[r] : typeof vec === 'number' ? vec : null;
        row[col] = storeCell(v == null || Number.isNaN(v) ? '' : formatMathResult(v));
        touched = true;
      });
    });
    if (touched) {
      lines.splice(b.start, b.end - b.start + 1, ...serializeTable(parsed).split('\n'));
    }
  }
  return lines.join('\n');
}

// Returns the KaTeX stylesheet currently loaded in this document so it
// can be embedded inline in the export. KaTeX is loaded via a <link>
// element pointing at `vendor/katex/katex.min.css`; we fetch it and
// rewrite the relative `fonts/...` URLs to absolute jsDelivr URLs so the
// exported HTML can render without the app's vendor tree.
/** @returns {Promise<string>} the inlined KaTeX stylesheet, or ''. */
async function getKatexCSS() {
  const link = document.querySelector('link[href*="vendor/katex/katex.min.css"]') as any;
  if (!link) return '';
  try {
    const res = await fetch(link.href);
    if (!res.ok) return '';
    const css = await res.text();
    // Rewrite relative font URLs to point at jsDelivr so the standalone
    // export works without needing the local vendor tree.
    return css.replace(
      /url\(fonts\//g,
      'url(https://cdn.jsdelivr.net/npm/katex@0.17.0/dist/fonts/'
    );
  } catch {
    return '';
  }
}

// CSS shared by single-note and notebook exports.  Uses the CSS-variable
// names resolved above so the output matches the active theme exactly.
const _EXPORT_SHARED_CSS = `
  *, *::before, *::after { box-sizing: border-box; }
  /* No visible scrollbars anywhere — exported docs follow the same project
     rule as the app (CLAUDE.md). base.css isn't loaded here, so the global
     hide is inlined: scrolling still works on pre/.katex/.mermaid-diagram,
     the chrome is just hidden. Keep in sync with web/css/base.css. */
  * { scrollbar-width: none !important; -ms-overflow-style: none !important; }
  *::-webkit-scrollbar { display: none !important; width: 0 !important; height: 0 !important; }
  a { color: var(--link); }
  h1 { color: var(--h1); border-bottom: 1px solid var(--accent); padding-bottom: 8px; margin-bottom: 12px; }
  h2 { color: var(--h2); }
  h3 { color: var(--h3); }
  h4 { color: var(--h4); }
  h5 { color: var(--h5); }
  h6 { color: var(--h6); }
  strong { color: var(--bold); }
  em { color: var(--italic); }
  del { color: var(--strike); }
  hr { border: none; border-top: 1px solid var(--accent); margin: 1em 0; }
  code { color: var(--code); background-color: var(--code-bg); padding: 0 4px; border-radius: 3px; font-family: 'Consolas','Monaco','Courier New',monospace; font-size: 0.9em; }
  pre { background-color: var(--code-block-bg); border: 1px solid var(--code-block-border); border-radius: 4px; padding: 10px; overflow-x: auto; margin: 0.75em 0; font-size: 0.9em; line-height: 1.5; tab-size: 4; -moz-tab-size: 4; }
  pre code { color: var(--code); background: none; padding: 0; border-radius: 0; font-size: 1em; }
  mark { background-color: var(--mark-bg); color: var(--mark-color); border-radius: 2px; padding: 0 2px; }
  mark * { color: var(--mark-color); }
  p { margin: 0.5em 0; }
  blockquote { margin: 12px 0; padding: 6px 14px; border-left: 3px solid var(--blockquote-border); background-color: var(--blockquote-bg); color: var(--blockquote-text); border-radius: 0 4px 4px 0; }
  blockquote p { margin: 0; }
  ul, ol { padding-left: 1.5em; margin: 0.5em 0; }
  li > ul, li > ol { margin: 0; }
  li { margin: 0.15em 0; }
  ul { list-style-type: disc; }
  ul ul { list-style-type: circle; }
  ul ul ul { list-style-type: square; }
  li p:first-child:last-child { margin: 0; }
  li.task-item + li.bullet-item, li.bullet-item + li.task-item { margin-top: 8px; }
  img { max-width: 100%; border: 1px solid var(--border); }
  table { border-collapse: collapse; margin: 0.75em 0; }
  th, td { border: 1px solid var(--border); padding: 6px 12px; max-width: 60ch; overflow-wrap: anywhere; vertical-align: top; }
  th.col-numeric, td.col-numeric { max-width: none; white-space: nowrap; overflow-wrap: normal; }
  thead tr { background-color: var(--table-header-bg); }
  tbody tr:nth-child(even) { background-color: var(--table-alt-row); }
  .footnote-ref { color: var(--footnote); text-decoration: none; font-size: 0.75em; vertical-align: super; }
  .footnote-hr { border: none; border-top: 1px solid var(--border); margin: 24px 0 12px; }
  .footnotes { list-style: none; padding: 0; font-size: 0.85em; color: var(--footnote); }
  .footnote-back { color: var(--footnote-back); text-decoration: none; margin-left: 4px; }
  details { margin: 0; }
  details > summary { list-style: none; cursor: pointer; }
  details > summary::-webkit-details-marker { display: none; }
  details > summary h2, details > summary h3, details > summary h4,
  details > summary h5, details > summary h6 { display: inline; margin: revert; }
  details > summary::after { content: ' \u203a'; font-size: 0.8em; color: var(--accent); }
  details[open] > summary::after { content: ' \u2304'; }
  .mermaid-diagram { overflow-x: auto; margin: 0.75em 0; }
  .mermaid-diagram svg { max-width: 100%; height: auto; }
  .katex-display { display: block; max-width: 100% !important; overflow-x: auto; overflow-y: hidden; margin: 0.5em 0; text-align: left; }
  .katex { max-width: 100% !important; overflow-x: auto; overflow-y: hidden; vertical-align: middle; }
  body .katex { font-size: 1em; }
  input[type="checkbox"] { appearance: none; -webkit-appearance: none; width: 14px; height: 14px; border: 1.5px solid var(--border); background-color: var(--code-bg); vertical-align: middle; margin-right: 4px; border-radius: 3px; position: relative; cursor: default; }
  input[type="checkbox"]:checked { background-color: var(--accent); border-color: var(--accent); }
  input[type="checkbox"]:checked::after { content: ''; position: absolute; left: 3px; top: 0px; width: 5px; height: 9px; border: 2px solid white; border-top: none; border-left: none; transform: rotate(45deg); }
  li.task-item { list-style-type: none; }
`;

// Runs the full rendering pipeline on a detached container:
//   Mermaid diagrams → SVG, then KaTeX render.
// Returns the KaTeX CSS for inlining into the export (empty string if
// KaTeX never loaded in this session).
/**
 * @param {any} container - a detached render container.
 * @returns {Promise<string>} the KaTeX CSS to inline, or ''.
 */
async function renderContainerForExport(container: any, markdown?: any) {
  // Substitute the note's math vars into mermaid sources so exported diagrams
  // match the live editor / preview (which already substitute). Pass both the
  // scalar varMap ($name$) AND the column env (the `@Col` series pass, P2) so a
  // table-driven `bar [@Revenue]` bakes its values into the export too. Without
  // this, a `$total$`- or `@Col`-driven diagram would export with literal text.
  await renderMermaidDiagrams(
    container,
    typeof markdown === 'string' ? buildMermaidVarMap(markdown) : undefined,
    typeof markdown === 'string' ? _buildNoteColumnEnv(markdown).env : undefined
  );

  if (!window.katex) {
    try {
      await ensureKatexLoaded();
    } catch {
      return '';
    }
  }
  if (!window.katex) return '';

  renderMathIn(container);
  return await getKatexCSS();
}

/**
 * @param {any} container
 * @param {string} noteName
 * @returns {Promise<void>}
 */
async function embedAttachmentsInHtml(container: any, noteName: any) {
  if (typeof window.NoteStorage.readAttachment !== 'function') return;
  const readFn = (n: any, f: any) => window.NoteStorage.readAttachment(n, f);
  // The marked image renderer emits <img data-attachment="…" alt="…"> for
  // attachment: URLs (CSP forbids the unsupported scheme on src=). Read the
  // filename from the data attribute and inline the bytes as a data URI.
  for (const img of container.querySelectorAll('img[data-attachment]')) {
    const filename = img.getAttribute('data-attachment');
    const b64 = await readFn(noteName, filename);
    if (b64) {
      const ext = filename.split('.').pop();
      img.src = `data:${mimeForExtension(ext)};base64,${b64}`;
    }
    img.removeAttribute('data-attachment');
  }
  for (const link of container.querySelectorAll('a[href^="attachment:"]')) {
    const filename = link.getAttribute('href').slice('attachment:'.length);
    const b64 = await readFn(noteName, filename);
    if (b64) {
      const ext = filename.split('.').pop();
      link.href = `data:${mimeForExtension(ext)};base64,${b64}`;
      link.setAttribute('download', filename);
    }
  }
}

/**
 * @param {string} title
 * @param {any} markdown
 * @param {string} [noteName]
 * @returns {Promise<string>} the standalone HTML document.
 */
export async function generateHtmlContent(title: any, markdown: any, noteName: any) {
  installMarkedConfig(); // ensure attachment: images render as <img data-attachment>
  const processedMarkdown = processNoteForExport(markdown);

  const container = document.createElement('div');
  container.innerHTML = safeRenderMarkdown(preprocessMarkdown(processedMarkdown));
  styleTaskListItems(container);
  if (noteName) await embedAttachmentsInHtml(container, noteName);
  alignTableColumns(container);
  setupCollapsibleHeadings(container);

  const katexCSS = await renderContainerForExport(container, markdown);

  const cssRoot = getExportThemeCSSRoot();
  const style = `
    :root { ${cssRoot} }
    body { max-width: 800px; margin: 20px auto; padding: 10px 20px; font-family: Arial, sans-serif; font-size: 16px; line-height: 1.5; background-color: var(--bg); color: var(--text); }
    ${_EXPORT_SHARED_CSS}
  `;

  const mathSection = _mathStyleSection(katexCSS);

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${_escAttr(title)}</title>
<style>${style}</style>
${mathSection}
</head>
<body>
${container.innerHTML}
</body>
</html>`;
}

/**
 * @param {string} name
 * @returns {string} a DOM-id-safe anchor for the note.
 */
function noteNameToId(name: any) {
  return (
    'note-' +
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
  );
}

/**
 * @param {any[]} noteEntries - [{ name, content }, …].
 * @returns {Promise<string>} the multi-note notebook HTML.
 */
export async function generateNotebookHtml(noteEntries: any) {
  installMarkedConfig(); // ensure attachment: images render as <img data-attachment>
  const noteNameSet = new Set(noteEntries.map((e: any) => e.name));

  const tocItems = noteEntries
    .map(({ name }: any) => `<li><a href="#${noteNameToId(name)}">${_escAttr(name)}</a></li>`)
    .join('\n      ');

  // Build and pre-process each note container sequentially (avoids mermaid ID
  // collisions that could occur with concurrent renders).
  const noteContainers = [];
  for (const { name, content } of noteEntries) {
    const processedContent = processNoteForExport(content);
    const container = document.createElement('div');
    container.innerHTML = safeRenderMarkdown(preprocessMarkdown(processedContent));
    styleTaskListItems(container);
    await embedAttachmentsInHtml(container, name);
    container.querySelectorAll('a').forEach((a) => {
      const href = a.getAttribute('href');
      if (!href || href.startsWith('#') || /^[a-zA-Z]+:/.test(href)) return;
      let noteName: string;
      try {
        noteName = decodeURIComponent(href).replace(/_/g, ' ').trim();
      } catch {
        noteName = href.replace(/_/g, ' ').trim();
      }
      if (noteNameSet.has(noteName)) {
        a.setAttribute('href', `#${noteNameToId(noteName)}`);
      }
    });
    alignTableColumns(container);
    setupCollapsibleHeadings(container);
    await renderMermaidDiagrams(
      container,
      buildMermaidVarMap(content),
      _buildNoteColumnEnv(content).env
    );
    noteContainers.push({ name, container });
  }

  // Render all math at once for efficiency.
  let katexCSS = '';
  if (noteContainers.length > 0) {
    if (!window.katex) {
      try {
        await ensureKatexLoaded();
      } catch {
        /* fall through */
      }
    }
    if (window.katex) {
      for (const { container } of noteContainers) {
        renderMathIn(container);
      }
      katexCSS = await getKatexCSS();
    }
  }

  const sections = noteContainers
    .map(
      ({ name, container }) =>
        `<article id="${noteNameToId(name)}" aria-label="${_escAttr(name)}">\n${container.innerHTML}\n</article>`
    )
    .join('\n\n');

  const cssRoot = getExportThemeCSSRoot();
  const style = `
    :root { ${cssRoot} }
    body { margin: 0; display: flex; height: 100vh; font-family: Arial, sans-serif; font-size: 16px; background-color: var(--bg); color: var(--text); }
    #toc { width: 220px; flex-shrink: 0; position: sticky; top: 0; height: 100vh; overflow-y: auto; border-right: 1px solid var(--border); padding: 20px 12px; background-color: var(--bg); }
    #toc h3 { margin: 0 0 12px; font-size: 13px; text-transform: uppercase; letter-spacing: 0.05em; color: var(--hr); }
    #toc ul { list-style: none; margin: 0; padding: 0; }
    #toc a { display: block; padding: 4px 6px; border-radius: 3px; color: var(--h4); text-decoration: none; font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    #toc a:hover { color: var(--text); background-color: var(--surface); }
    main { flex: 1; overflow-y: auto; padding: 40px; }
    article { max-width: 800px; margin: 0 auto 60px; }
    article + article { border-top: 1px solid var(--border); padding-top: 40px; }
    ${_EXPORT_SHARED_CSS}
  `;

  const mathSection = _mathStyleSection(katexCSS);

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Notes Notebook</title>
<style>${style}</style>
${mathSection}
</head>
<body>
<nav id="toc">
  <h3>Contents</h3>
  <ul>
      ${tocItems}
  </ul>
</nav>
<main>
${sections}
</main>
</body>
</html>`;
}
